import React, { useEffect, useRef } from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { useKeyDown } from "@react-hooks-library/core";
import { AxiosError } from "axios";
import { useForm } from "react-hook-form";

import {
  VERSION_DESCRIPTION_MAX_LENGTH,
  VERSION_NAME_MAX_LENGTH,
  VERSION_NAME_MIN_LENGTH,
} from "src/agents/constants";
import { AgentVersion } from "src/agents/types";
import { useUpdateAgentVersion } from "src/api/agents/management";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Textarea } from "src/base-components/Textarea";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { logger } from "src/utils/logger";

type EditAgentVersionModalProps = {
  open: boolean;
  onClose: () => void;
  version: AgentVersion | null;
};

type EditAgentVersionForm = {
  name: string;
  description: string;
};

export const EditAgentVersionModal: React.FC<EditAgentVersionModalProps> = ({
  open,
  onClose,
  version,
}) => {
  const { mutateAsync: updateVersion } = useUpdateAgentVersion();
  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<EditAgentVersionForm>({
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
    },
  });

  useEffect(() => {
    if (!open) return; // Prevent form reset when modal is closed so it doesn't flicker
    if (version) {
      form.reset({
        name: version.name,
        description: version.description,
      });
    } else {
      form.reset({
        name: "",
        description: "",
      });
    }
  }, [version, form, open]);

  // Not taking advantage of the native cmd+enter form functionality
  // because it doesn't work with textarea (which we use for the description)
  useKeyDown(
    ["Enter"],
    (e) => {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        formRef.current?.requestSubmit();
      }
    },
    { target: formRef },
  );

  const onSubmit = async (data: EditAgentVersionForm) => {
    if (!version) return;

    try {
      await updateVersion({
        agentId: version.agent_id,
        versionId: version.id,
        etag: version.etag,
        name: data.name,
        description: data.description,
        configuration: version.configuration,
      });
      toastActions.success({
        title: "Version updated successfully",
      });
      onClose();
    } catch (error) {
      logger.error("Failed to update version", error);
      const errorMessage =
        error instanceof AxiosError && error.status === 409
          ? "Version name already exists"
          : "An unknown error occurred";
      toastActions.failure({
        title: "Failed to update version",
        description: errorMessage,
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>Edit version</Modal.Header>
      <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="flex flex-col gap-5">
            <div>
              <Label required>Version name</Label>
              <Input
                errored={!!form.formState.errors.name}
                placeholder="Type version name"
                fullWidth
                {...form.register("name", {
                  required: "Version name is required",
                  minLength: {
                    value: VERSION_NAME_MIN_LENGTH,
                    message: `Version name must be at least ${VERSION_NAME_MIN_LENGTH} character`,
                  },
                  maxLength: {
                    value: VERSION_NAME_MAX_LENGTH,
                    message: `Version name must be less than ${VERSION_NAME_MAX_LENGTH} characters`,
                  },
                })}
              />
              {form.formState.errors.name && (
                <ErrorHint>{form.formState.errors.name.message}</ErrorHint>
              )}
            </div>

            <div>
              <div className="mb-2 flex items-center gap-1">
                <Label mb="mb-0">Description</Label>
                <Tooltip
                  body="Adding a description helps others understand what this version does and how it differs from other versions."
                  placement="right-start"
                >
                  <Icon icon={faInfoCircle} size="xs" />
                </Tooltip>
              </div>
              <Textarea
                errored={!!form.formState.errors.description}
                placeholder="Type version description"
                fullWidth
                {...form.register("description", {
                  maxLength: {
                    value: VERSION_DESCRIPTION_MAX_LENGTH,
                    message: `Description must be less than ${VERSION_DESCRIPTION_MAX_LENGTH} characters`,
                  },
                })}
              />
              {form.formState.errors.description && (
                <ErrorHint>
                  {form.formState.errors.description.message}
                </ErrorHint>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              isDisabled={
                form.formState.isSubmitting || !form.formState.isValid
              }
              htmlType="submit"
              isLoading={form.formState.isSubmitting}
              variant="primary"
            >
              Save
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
